import React from "react";
import {
  TrendingUp,
  FileText,
  Home,
  BarChart3,
  Wallet,
  Clock,
  ShieldCheck,
} from "lucide-react";
import OverviewKpiCard from "./OverviewKpiCard";
import OverviewOccupancyCard from "./OverviewOccupancyCard";
import {
  selectCashIn,
  selectExpectedContribution,
  selectExpectedRevenue,
  selectInvoiced,
  selectOpenReceivables,
  selectOperatingCosts,
  selectTenantCashDeposits,
} from "./overviewSelectors";
import OverviewPeriodUnsupported from "./OverviewPeriodUnsupported";

/**
 * The KPI surface: four headline figures, then four secondary ones.
 *
 * THE SUBTEXTS ARE THE CONTRACT
 * -----------------------------
 * These figures sit on FOUR different time axes and look identical:
 *
 *   expected revenue   → the selected SERVICE month
 *   invoiced           → the selected INVOICE month (`issue_date`), which is not the
 *                        same month: Vantio issues on the 15th of the PRECEDING month
 *   cash-in            → payments received in the selected month
 *   open receivables   → CURRENT STATE, no month at all
 *   cash deposits held → CURRENT STATE, no month at all
 *   occupancy          → BOTH: today's rate and the selected month's average, which
 *                        is why that tile is its own component (B1e)
 *
 * Placed under a header reading "August 2026", a current-state figure silently claims
 * to be August's. The subtext is the only thing preventing that, which is why every
 * card has one and why they say "Aktueller Stand" rather than nothing.
 *
 * P3b MADE THOSE SUBTEXTS MOVE WITH THE PERIOD. The four period figures used to say
 * "im ausgewählten Monat" unconditionally; under an April–September filter that was
 * a false statement about a figure that had itself become a false statement. The
 * axis wording is unchanged — invoiced still says `Rechnungsdatum`, cash-in still
 * says `Zahlungen` — only the span it names. The two current-state cards deliberately
 * do NOT move: an invoice open today is open today whichever period is selected.
 *
 * NO TREND ROW. The reference design shows "↑ 32.5% vs. Vormonat" on every card.
 * Real comparison figures exist for the revenue-analytics KPIs only, and inventing
 * the rest would be the one thing this whole programme has been avoiding. Trends
 * arrive in B1b for the cards that can actually source them.
 */

const fmtChf = (n) =>
  n === null || n === undefined
    ? null
    : `CHF ${new Intl.NumberFormat("de-CH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(n))}`;

/**
 * The span a period figure names, as the end of a sentence.
 *
 * One month reads exactly as it always did; a window says so. The alternative — a
 * card that says "Monat" under a filter reading "April – September 2026" — is the
 * specific dishonesty P3b removes, and it is not fixed by changing the number alone.
 */
const span = (isRange) => (isRange ? "im gewählten Zeitraum" : "im ausgewählten Monat");

/** What a period card shows when the period has no answerable window. */
const UNSUPPORTED_HINT = "Zeitraum wählen";

export default function OverviewKpiGrid({
  revenueAnalytics,
  companyInvoiced,
  costBreakdown,
  occupancyRoomDays,
  currentOccupancy,
  window: periodWindow,
  unsupported = false,
  cashDeposits,
  cashDepositsLoading = false,
  onOpenCashDepositDetails,
  periodicLoading,
  currentLoading = false,
  onRetryPeriodic,
  onRetryCurrent,
}) {
  const isRange = Boolean(periodWindow?.isRange);

  // NOTHING FROM THE FLOW BLOCKS IS READ WHEN THE PERIOD HAS NO WINDOW. "Gesamte
  // Zeit" still issues one analytics request — it carries the current-state
  // receivables — and its expected/cash-in figures answer for a month the user did
  // not select, so they are not selected here at all rather than selected and then
  // hidden.
  const expected = unsupported ? null : selectExpectedRevenue(revenueAnalytics?.data);
  const invoiced = unsupported ? null : selectInvoiced(companyInvoiced?.data);
  const costs = unsupported ? null : selectOperatingCosts(costBreakdown?.data);
  const contribution = selectExpectedContribution(expected, costs);

  const cashIn = unsupported ? null : selectCashIn(revenueAnalytics?.data);
  // CURRENT STATE, and therefore answered in every mode — including "Gesamte Zeit",
  // where it is the one money figure that still means something.
  const open = selectOpenReceivables(revenueAnalytics?.data);
  // TENANT deposits — money held FOR a tenant, from /deposits/cash-held. Not the
  // landlord deposit we place with an owner (A2b), and never summed with it: one is
  // a liability, the other bound capital.
  const cashDepositsTotal = selectTenantCashDeposits(cashDeposits);

  // The contribution has two upstreams, so it is unavailable when EITHER fails —
  // and saying which one is more useful than a generic error.
  const contributionError =
    revenueAnalytics?.error || costBreakdown?.error ? "unavailable" : "";

  // An unsupported period is not a failed one: nothing is retried, and the cards say
  // what to do instead of "Nicht verfügbar".
  const periodSubtext = (text) => (unsupported ? UNSUPPORTED_HINT : text);
  const periodError = (error) => (unsupported ? "" : error);
  const periodRetry = unsupported ? undefined : onRetryPeriodic;

  return (
    <div className="space-y-3">
      {unsupported ? <OverviewPeriodUnsupported /> : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OverviewKpiCard
          testId="kpi-expected-revenue"
          title="Erwarteter Umsatz"
          value={fmtChf(expected)}
          subtext={periodSubtext(`Vertrags-Soll ${span(isRange)}`)}
          icon={<TrendingUp className="h-4 w-4" />}
          accent="blue"
          loading={periodicLoading}
          error={periodError(revenueAnalytics?.error)}
          onRetry={periodRetry}
        />
        <OverviewKpiCard
          testId="kpi-invoiced"
          title="Fakturiert"
          value={fmtChf(invoiced)}
          subtext={periodSubtext(`Rechnungsdatum ${span(isRange)}`)}
          icon={<FileText className="h-4 w-4" />}
          accent="indigo"
          loading={periodicLoading}
          error={periodError(companyInvoiced?.error)}
          onRetry={periodRetry}
        />
        <OverviewKpiCard
          testId="kpi-operating-costs"
          title="Betriebskosten"
          value={fmtChf(costs)}
          subtext={periodSubtext(
            isRange ? "Für den gewählten Zeitraum" : "Für den ausgewählten Monat"
          )}
          icon={<Home className="h-4 w-4" />}
          accent="amber"
          loading={periodicLoading}
          error={periodError(costBreakdown?.error)}
          onRetry={periodRetry}
        />
        <OverviewKpiCard
          testId="kpi-expected-contribution"
          title="Erwarteter Deckungsbeitrag"
          value={fmtChf(contribution)}
          // Period-neutral by construction: both operands are the SAME period, so the
          // difference needs no span of its own — and a mixed pair could never be
          // described by one anyway. That is the invariant, not a wording choice.
          subtext={periodSubtext("Soll-Miete minus Betriebskosten")}
          icon={<BarChart3 className="h-4 w-4" />}
          accent="emerald"
          loading={periodicLoading}
          error={periodError(contributionError)}
          onRetry={periodRetry}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OverviewKpiCard
          testId="kpi-cash-in"
          title="Zahlungseingang"
          value={fmtChf(cashIn)}
          subtext={periodSubtext(`Zahlungen ${span(isRange)}`)}
          icon={<Wallet className="h-4 w-4" />}
          accent="sky"
          compact
          loading={periodicLoading}
          error={periodError(revenueAnalytics?.error)}
          onRetry={periodRetry}
        />
        <OverviewKpiCard
          testId="kpi-open-receivables"
          title="Offene Forderungen"
          value={fmtChf(open)}
          // NOT a period figure, and the subtext is the only thing that says so. It
          // stays "Aktueller Stand" under every selection — a range must never make
          // this card read as the sum of six months of open invoices.
          subtext="Aktueller Stand"
          icon={<Clock className="h-4 w-4" />}
          accent="rose"
          compact
          loading={periodicLoading}
          error={revenueAnalytics?.error}
          onRetry={onRetryPeriodic}
        />
        {/* Two sources, two loading flags, two error states — see the component. */}
        <OverviewOccupancyCard
          currentOccupancy={currentOccupancy}
          occupancyRoomDays={occupancyRoomDays}
          window={periodWindow}
          unsupported={unsupported}
          currentLoading={currentLoading}
          periodicLoading={periodicLoading}
          onRetryCurrent={onRetryCurrent}
          onRetryPeriodic={onRetryPeriodic}
        />
        <OverviewKpiCard
          testId="kpi-cash-deposits"
          title="Erhaltene Barkautionen"
          value={fmtChf(cashDepositsTotal)}
          subtext="Hinterlegte Barkautionen · Aktueller Stand"
          icon={<ShieldCheck className="h-4 w-4" />}
          accent="teal"
          compact
          loading={cashDepositsLoading}
          footer={
            onOpenCashDepositDetails ? (
              <button
                type="button"
                onClick={onOpenCashDepositDetails}
                className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              >
                Details anzeigen
              </button>
            ) : null
          }
        />
      </div>
    </div>
  );
}
